/**
 * @file `LearnersPanel` — root component of the `/learners` route.
 * @module @labcraft/admin/ui/learners/learners-panel
 *
 * Read-only view of the learner queue. Owns:
 *
 *   - Reading one page of learners via `useLearners({ status, q, sort,
 *     dir, limit, offset })` — an offset-paged query returning `{ learners,
 *     total }`. The page size is a function of the viewport: `usePageSize`
 *     measures the table's scroll container and returns exactly the number
 *     of rows that fit, recomputed on resize, so a page fills the real
 *     estate instead of showing a fixed N in a scroll box. The shared
 *     `<Pager>` navigates numbered pages; each page *replaces* the rows, so
 *     the table never scrolls.
 *   - Local filter + sort + page state. Sort starts absent so the daemon's
 *     status-derived default holds; a status change clears an
 *     operator-selected sort. A control change (status / q / sort / tags)
 *     resets to page 1; a page-size change (viewport resize, or the detail
 *     pane opening and shrinking the table) instead re-anchors the current
 *     page on its first visible row via `anchorPage`, so opening a learner
 *     never loses the operator's place.
 *   - Selected-row state. Selection is URL-encoded via `?l=<id>` so the
 *     detail drawer is linkable. Closing the drawer drops the query param.
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type JSX,
} from 'react'
import { useSearchParams } from 'react-router'
import type {
  LearnerStatus,
  LearnerSortField,
  LearnerSortDir,
} from '../../shared/routes.ts'
import {
  useAllTags,
  useLearner,
  useLearners,
  usePagedList,
} from '../hooks/index.ts'
import { LowerSplit } from '../kernel/lower_split.tsx'
import { Pager } from '../kernel/pager.tsx'
import { LearnersFilters } from './learners_filters.tsx'
import { LearnersTable } from './learners_table.tsx'
import { LearnerDetail } from './learner_detail.tsx'

type StatusFilter = LearnerStatus | 'all'

/** Render the Learners route. */
export function LearnersPanel(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams()
  const [status, setStatus] = useState<StatusFilter>('all')
  const [query, setQuery] = useState('')
  // Sort starts absent so the daemon's status-derived default holds
  // (inactive → oldest-signup first; else most-recent-login first). Only
  // an explicit header click sends a sort. `dir` is meaningful only while
  // `sort` is set.
  const [sort, setSort] = useState<LearnerSortField | null>(null)
  const [dir, setDir] = useState<LearnerSortDir>('desc')
  // Tag include/exclude filters (#1639). Raw strings; the account daemon
  // canonicalizes. Any change resets to page 1 (like every other control).
  const [tags, setTags] = useState<string[]>([])
  const [tagsNot, setTagsNot] = useState<string[]>([])
  const allTags = useAllTags()

  // Pagination via the shared hook: page size + exact row height are derived
  // from the scroll container's height (usePagedList → usePageSize) so a page
  // fills the viewport with no scrollbar and no gap above the pager, and a
  // page-size change (resize, or the detail pane opening) re-anchors the first
  // visible row instead of snapping to page 1. `total` is the server's count
  // across all pages; it drives pageCount/clamp only, so it is synced from the
  // query result on the next render — the query needs pageSize/offset, which
  // usePagedList produces without needing total (no chicken-and-egg).
  const scrollRef = useRef<HTMLDivElement>(null)
  const [total, setTotal] = useState(0)
  // Absolute index of the selected row, captured at click. While a detail is
  // open this anchors the re-page on size change so the selected learner stays
  // visible and highlighted next to its detail (not paged away).
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const { page, setPage, resetPage, pageSize, rowHeight, offset, pageCount } =
    usePagedList(scrollRef, total, { anchorIndex: selectedIndex })

  const result = useLearners({
    ...(status !== 'all' ? { status } : {}),
    ...(query ? { q: query } : {}),
    ...(sort ? { sort, dir } : {}),
    ...(tags.length > 0 ? { tags } : {}),
    ...(tagsNot.length > 0 ? { tagsNot } : {}),
    limit: pageSize,
    ...(offset > 0 ? { offset } : {}),
  })
  const learners = result.data?.learners ?? null
  const serverTotal = result.data?.total ?? 0
  useEffect(() => {
    setTotal(serverTotal)
  }, [serverTotal])
  const loading = result.isPending
  const error = result.isError
    ? result.error.message || 'Could not load learners.'
    : null

  // Changing the status filter clears an operator-selected sort, reverting
  // to the status-derived default (per the locked #1624 decision).
  const onStatus = useCallback(
    (next: StatusFilter) => {
      setStatus(next)
      setSort(null)
      resetPage()
    },
    [resetPage],
  )
  const onQuery = useCallback(
    (next: string) => {
      setQuery(next)
      resetPage()
    },
    [resetPage],
  )
  const onTags = useCallback(
    (next: string[]) => {
      setTags(next)
      resetPage()
    },
    [resetPage],
  )
  const onTagsNot = useCallback(
    (next: string[]) => {
      setTagsNot(next)
      resetPage()
    },
    [resetPage],
  )

  // Header click: toggle direction on the active field, else switch to the
  // clicked field at its natural first-click direction (names A→Z,
  // timestamps newest-first).
  const onSort = useCallback(
    (field: LearnerSortField) => {
      if (sort === field) {
        setDir(d => (d === 'asc' ? 'desc' : 'asc'))
      } else {
        setSort(field)
        setDir(field === 'displayName' ? 'asc' : 'desc')
      }
      resetPage()
    },
    [sort, resetPage],
  )

  // Resolve the selected learner across *all* cached pages, not just the
  // current one. Opening the detail shrinks the table, which re-pages the
  // list and can push the selected row off the visible page; a current-page
  // lookup would then go null → close the detail → grow the table → re-page
  // → reopen, an infinite flicker. The cache-wide `useLearner` keeps the
  // detail stable while the list below re-pages underneath it.
  const selectedId = searchParams.get('l')
  const selectedFromCache = useLearner(selectedId ?? '')
  const selected = selectedId ? (selectedFromCache ?? null) : null

  const openDetail = useCallback(
    (id: string) => {
      // Capture the row's absolute index so the size-change re-page (the
      // detail pane opening + shrinking the table) keeps this row on the
      // visible page instead of anchoring the first row and paging it away.
      const idx = learners?.findIndex(l => l.id === id) ?? -1
      setSelectedIndex(idx >= 0 ? offset + idx : null)
      const next = new URLSearchParams(searchParams)
      next.set('l', id)
      setSearchParams(next, { replace: false })
    },
    [learners, offset, searchParams, setSearchParams],
  )
  const closeDetail = useCallback(() => {
    setSelectedIndex(null)
    const next = new URLSearchParams(searchParams)
    next.delete('l')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  // Esc closes the lower detail pane.
  useEffect(() => {
    if (!selectedId) return undefined
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') closeDetail()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
    }
  }, [selectedId, closeDetail])

  return (
    <div className="panel learners-panel">
      <header className="panel-head">
        <div>
          <div className="panel-head__title-row">
            <h1 className="panel-head__title">Learners</h1>
            <span className="panel-head__count">
              {result.data ? `${serverTotal.toLocaleString()} total` : '—'}
            </span>
          </div>
          <p className="panel-head__sub">
            // active, inactive, banned — one queue.
          </p>
        </div>
      </header>
      <LearnersFilters
        status={status}
        onStatus={onStatus}
        query={query}
        onQuery={onQuery}
        tags={tags}
        onTags={onTags}
        tagsNot={tagsNot}
        onTagsNot={onTagsNot}
        tagSuggestions={allTags.data ?? []}
      />
      <LowerSplit
        open={selected !== null}
        storageKey="lc-learners-split"
        detail={
          selected !== null && (
            <LearnerDetail learner={selected} onClose={closeDetail} />
          )
        }>
        <div className="paged-list">
          {error !== null && (
            <div className="panel-banner panel-banner--error">
              <b>Could not load learners.</b> {error}
            </div>
          )}
          <div
            className="table-scroll"
            ref={scrollRef}
            style={
              rowHeight > 0
                ? ({ '--row-h': `${rowHeight}px` } as CSSProperties)
                : undefined
            }>
            <LearnersTable
              learners={learners}
              loading={loading}
              selectedId={selectedId}
              onSelect={openDetail}
              sort={sort}
              dir={dir}
              onSort={onSort}
            />
          </div>
          <div className="paged-foot">
            <Pager
              page={page}
              pageCount={pageCount}
              onPage={setPage}
              total={result.data ? serverTotal : undefined}
            />
          </div>
        </div>
      </LowerSplit>
    </div>
  )
}
